import * as THREE from 'three';
import { onGpu } from './gpu';

/**
 * 데칼 — 삼각형 두 장으로 조명을 흉내 내는 장치.
 *
 * 밤 씬을 살리는 것은 접지 그림자와 빛 웅덩이인데, 둘 다 진짜로 하면 비싸다.
 * 이 마을은 방마다 그림자를 켜는 등이 **최대 두 개**고(world.ts) 바깥은 하나도
 * 없다. 그래서 물건이 전부 바닥에 떠 있는 것처럼 보였고, 가로등은 켜져 있는지
 * 조차 알 수 없었다 — outside.ts 의 주석이 그 기록이다(「등 아래에 빛 웅덩이가
 * 없었다」).
 *
 * 여기서 파는 것은 셋이다.
 *   - 접지 그림자 : 물건 밑에 까는 검은 원. 알파만 있는 판이라 바닥을 눌러 준다
 *   - 빛 웅덩이   : 등 밑에 까는 밝은 원. 가산 합성
 *   - 빛 기둥     : 등에서 내려오는 원뿔. 가산 + 정점색이 아래로 검게 죽는다
 *
 * 셋 다 **한 덩어리로 합쳐 한 번에 그린다**. 개수가 늘어도 draw call 은 그대로다.
 * 정점색은 vec4 로 쓴다 — three 는 color 어트리뷰트가 4성분이면 알파까지 읽는다.
 * 세기를 정점 알파에 구우면 물건마다 재질을 만들지 않아도 된다.
 */

/** 가운데가 희고 가장자리가 검은 원. 그림자는 이걸 알파로, 빛은 색으로 쓴다 */
function radialTexture(): THREE.Texture {
  const S = 128;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  // 0.62 지점까지 살을 남겨야 「원」이 아니라 「번짐」으로 읽힌다.
  // 선형으로 떨구면 가장자리에 또렷한 링이 보인다
  grd.addColorStop(0, '#ffffff');
  grd.addColorStop(0.28, '#c8c8c8');
  grd.addColorStop(0.58, '#4e4e4e');
  grd.addColorStop(0.82, '#101010');
  grd.addColorStop(1, '#000000');
  g.fillStyle = grd;
  g.fillRect(0, 0, S, S);
  const t = new THREE.CanvasTexture(c);
  // 알파/세기용이지 색이 아니다. sRGB 변환을 걸면 감마가 두 번 먹는다
  t.colorSpace = THREE.NoColorSpace;
  return t;
}

let sharedTex: THREE.Texture | null = null;
function tex() {
  if (!sharedTex) sharedTex = radialTexture();
  return sharedTex;
}

/** 바닥에 눕힌 사각형 하나. rgba 는 정점색으로 굽는다 */
function pushQuad(
  P: number[],
  U: number[],
  C: number[],
  I: number[],
  x: number,
  y: number,
  z: number,
  r: number,
  cr: number,
  cg: number,
  cb: number,
  ca: number,
) {
  const b = P.length / 3;
  // 위에서 보는 면이므로 반시계로 감는다 (법선 +Y)
  P.push(x - r, y, z + r, x + r, y, z + r, x + r, y, z - r, x - r, y, z - r);
  U.push(0, 0, 1, 0, 1, 1, 0, 1);
  for (let i = 0; i < 4; i++) C.push(cr, cg, cb, ca);
  I.push(b, b + 1, b + 2, b, b + 2, b + 3);
}

function quadGeo(P: number[], U: number[], C: number[], I: number[]) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(U, 2));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(C, 4));
  geo.setIndex(I);
  geo.computeBoundingSphere();
  return geo;
}

/**
 * 접지 그림자 모음. `add` 로 모아 두고 마지막에 `flush` 한 번.
 *
 * 진짜 그림자가 아니라 **물건이 바닥에 닿았다는 신호**다. 세기를 올리면
 * 물건 밑에 검은 구멍이 뚫리므로 0.55 언저리가 상한이다.
 */
/**
 * **데칼은 GTAO 에 보이면 안 된다.**
 *
 * `GTAOPass` 는 깊이·법선을 따로 한 번 더 굽는데, 그때 `scene.overrideMaterial`
 * 을 걸어 **모든** 메쉬를 불투명하게 그린다. `transparent`·`depthWrite:false`
 * 는 그 자리에서 통째로 무시된다. 그래서 바닥에서 12mm 뜬 지름 11m 짜리
 * 빛 웅덩이 판이 깊이 버퍼에 도로를 덮어 버렸고, GTAO 가 그 판을 실제 면으로
 * 읽어 `blendIntensity 1.15` 로 눌러서 **환상로·대로가 rgb(0,0,0) 인 구덩이**
 * 로 나왔다. 픽셀을 찍어 확인한 값이 정확히 0 이었다.
 *
 * 레이어로 뺀다 — 파이프라인이 GTAO 를 부르기 직전에 카메라에서 이 레이어만
 * 끄면 되고, 방을 다시 지어도 등록 목록을 관리할 필요가 없다.
 */
export const DECAL_LAYER = 3;

export class ContactShadows {
  private P: number[] = [];
  private U: number[] = [];
  private C: number[] = [];
  private I: number[] = [];

  /**
   * @param r 반지름(m). 물건 바닥 반쪽 폭의 1.2 배쯤이 자연스럽다
   * @param strength 0~1. 작고 가벼운 물건일수록 옅게
   */
  add(x: number, y: number, z: number, r: number, strength = 0.5) {
    if (r <= 0.02) return;
    pushQuad(this.P, this.U, this.C, this.I, x, y, z, r, 0, 0, 0, THREE.MathUtils.clamp(strength, 0, 1));
  }

  get count() {
    return this.I.length / 6;
  }

  /** 모아 둔 것을 한 메시로 굽는다. 하나도 없으면 아무것도 안 만든다 */
  flush(parent: THREE.Object3D) {
    if (!this.I.length) return null;
    const mesh = new THREE.Mesh(
      quadGeo(this.P, this.U, this.C, this.I),
      new THREE.MeshBasicMaterial({
        // 색은 검정 고정. 알파만 텍스처와 정점에서 온다
        color: 0x000000,
        alphaMap: tex(),
        transparent: true,
        depthWrite: false,
        vertexColors: true,
        toneMapped: false,
        // 바닥과 같은 평면이라 깊이가 붙는다. 살짝 당겨 z-fighting 을 막는다
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
    );
    mesh.name = '접지 그림자';
    mesh.layers.set(DECAL_LAYER);
    mesh.renderOrder = 2;
    mesh.matrixAutoUpdate = false;
    parent.add(mesh);
    return mesh;
  }
}

/**
 * 빛 웅덩이 + 빛 기둥.
 *
 * 기둥은 「공기 중의 눈에 빛이 걸린 것」이다. 진짜 볼류메트릭은 레이마칭이지만
 * 밤·눈·안개가 이미 깔린 화면에서는 **가산 원뿔 한 겹**과 거의 구별되지 않는다.
 * 대신 값이 세면 즉시 젤리 고깔이 되므로, 위쪽만 밝히고 아래는 검게 죽인다.
 */
/**
 * **빛기둥이 「정삼각형 피라미드」로 보이던 것을 고친다.**
 *
 * 알파가 높이(`t³`)로만 줄고 둘레 방향으로는 일정해서 **실루엣 가장자리가 칼같았다.**
 * 등이 여섯이면 같은 삼각형이 여섯 개 선다(2026-09-06 Chris 지적). 전부터 있었는데
 * 뒤가 밝은 안개일 때는 묻혔다가, 먼 능선을 어둡게 만들자 튀어나왔다.
 *
 * 빛이 든 원뿔 부피를 옆에서 보면 **가운데는 빛이 지나는 길이가 제일 길고 가장자리는
 * 0** 이다. 그 물리가 빠져 있었다. 면 법선과 시선이 이루는 각으로 알파를 깎으면
 * 가장자리가 저절로 녹는다 — 안쪽을 볼 때(정면) 1, 스치듯 볼 때(가장자리) 0.
 *
 * `MeshBasicMaterial` 에는 법선 varying 이 없으므로 직접 실어 보낸다.
 * `applyTinWeathering` 이 월드 좌표를 넘기는 것과 같은 방식이다.
 */
function coneMaterial() {
  const m = new THREE.MeshBasicMaterial({
    blending: THREE.AdditiveBlending,
    transparent: true,
    depthWrite: false,
    // 안쪽 면도 보여야 등 밑을 지나갈 때 기둥이 사라지지 않는다
    side: THREE.DoubleSide,
    vertexColors: true,
    toneMapped: false,
  });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = 'varying vec3 dcN;\nvarying vec3 dcV;\n' + sh.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      dcN = normalize(normalMatrix * normal);
      dcV = -(modelViewMatrix * vec4(transformed, 1.0)).xyz;`,
    );
    sh.fragmentShader = 'varying vec3 dcN;\nvarying vec3 dcV;\n' + sh.fragmentShader.replace(
      '#include <color_fragment>',
      `#include <color_fragment>
      {
        float rim = abs(dot(normalize(dcN), normalize(dcV)));
        diffuseColor.a *= rim * rim;
      }`,
    );
  };
  m.customProgramCacheKey = () => 'lightCone';
  onGpu(m, (n, mm) => n.coneRim(mm));   // WebGPU 판(gpuNodes.ts)
  return m;
}

export class LightPools {
  private P: number[] = [];
  private U: number[] = [];
  private C: number[] = [];
  private I: number[] = [];
  private cones: THREE.BufferGeometry[] = [];
  private tmp = new THREE.Color();

  /** 바닥에 까는 원 */
  pool(x: number, y: number, z: number, r: number, color: THREE.ColorRepresentation, strength = 1) {
    this.rect(x, y, z, r, r, color, strength);
  }

  /**
   * 눌린 원. 창에서 새는 빛처럼 한쪽으로 길게 늘어나는 자국에 쓴다.
   * @param rx x 반폭, @param rz z 반폭
   */
  rect(
    x: number,
    y: number,
    z: number,
    rx: number,
    rz: number,
    color: THREE.ColorRepresentation,
    strength = 1,
  ) {
    const c = this.tmp.set(color);
    const a = THREE.MathUtils.clamp(strength, 0, 1);
    const b = this.P.length / 3;
    this.P.push(x - rx, y, z + rz, x + rx, y, z + rz, x + rx, y, z - rz, x - rx, y, z - rz);
    this.U.push(0, 0, 1, 0, 1, 1, 0, 1);
    for (let i = 0; i < 4; i++) this.C.push(c.r, c.g, c.b, a);
    this.I.push(b, b + 1, b + 2, b, b + 2, b + 3);
  }

  /**
   * 등에서 바닥으로 내려오는 원뿔.
   * @param topR 등 쪽 반지름, @param botR 바닥 쪽 반지름
   */
  cone(
    x: number,
    yTop: number,
    z: number,
    height: number,
    topR: number,
    botR: number,
    color: THREE.ColorRepresentation,
    strength = 1,
  ) {
    // openEnded — 뚜껑을 덮으면 위에서 봤을 때 밝은 원판이 뜬다
    const g = new THREE.CylinderGeometry(topR, botR, height, 14, 1, true);
    g.translate(x, yTop - height / 2, z);
    const p = g.attributes.position;
    const arr = new Float32Array(p.count * 4);
    const base = this.tmp.set(color);
    const k = THREE.MathUtils.clamp(strength, 0, 1);
    for (let i = 0; i < p.count; i++) {
      const t = (p.getY(i) - (yTop - height)) / height; // 0 = 바닥, 1 = 등
      // 아래로 갈수록 사라진다. 세제곱을 걸어야 「고깔」이 아니라 「번짐」이 된다
      const f = t * t * t;
      arr[i * 4] = base.r;
      arr[i * 4 + 1] = base.g;
      arr[i * 4 + 2] = base.b;
      arr[i * 4 + 3] = f * k;
    }
    g.setAttribute('color', new THREE.BufferAttribute(arr, 4));
    // 법선은 `CylinderGeometry` 가 준 것을 그대로 둔다 — flush 에서 시선각 감쇠에 쓴다
    this.cones.push(g);
  }

  flush(parent: THREE.Object3D) {
    if (this.I.length) {
      const m = new THREE.Mesh(
        quadGeo(this.P, this.U, this.C, this.I),
        new THREE.MeshBasicMaterial({
          map: tex(),
          blending: THREE.AdditiveBlending,
          transparent: true,
          depthWrite: false,
          vertexColors: true,
          toneMapped: false,
          polygonOffset: true,
          polygonOffsetFactor: -3,
          polygonOffsetUnits: -3,
        }),
      );
      m.name = '빛 웅덩이';
      m.layers.set(DECAL_LAYER);
      m.renderOrder = 3;
      m.matrixAutoUpdate = false;
      parent.add(m);
    }
    if (this.cones.length) {
      let vTotal = 0;
      let iTotal = 0;
      for (const g of this.cones) {
        vTotal += g.attributes.position.count;
        iTotal += g.index!.count;
      }
      const P = new Float32Array(vTotal * 3);
      const Nn = new Float32Array(vTotal * 3);
      const C = new Float32Array(vTotal * 4);
      const I = new Uint32Array(iTotal);
      let vo = 0;
      let io = 0;
      for (const g of this.cones) {
        const p = g.attributes.position as THREE.BufferAttribute;
        const nn = g.attributes.normal as THREE.BufferAttribute;
        const c = g.attributes.color as THREE.BufferAttribute;
        P.set(p.array as Float32Array, vo * 3);
        Nn.set(nn.array as Float32Array, vo * 3);
        C.set(c.array as Float32Array, vo * 4);
        const src = g.index!.array;
        for (let i = 0; i < src.length; i++) I[io + i] = src[i] + vo;
        vo += p.count;
        io += src.length;
        g.dispose();
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(P, 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(Nn, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(C, 4));
      geo.setIndex(new THREE.BufferAttribute(I, 1));
      geo.computeBoundingSphere();
      const m = new THREE.Mesh(
        geo,
        coneMaterial(),
      );
      m.name = '빛 기둥';
      m.layers.set(DECAL_LAYER);
      m.renderOrder = 4;
      m.matrixAutoUpdate = false;
      parent.add(m);
    }
    this.cones = [];
  }
}
